import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { DEFAULT_PAGE_SIZE, type AssetNetwork, InvoiceStatus, InvoiceSubStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/status-badge";
import { Amount, FiatAmount, formatFiatAmount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { MerchantCell } from "@/components/merchant-cell";
import { INVOICE_STATUS_COLOR, REFUND_STATUS_COLOR } from "@/lib/status-colors";
import { formatStatusLabel } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { exportCsv } from "@/lib/csv";
import { acceptAmlCase, getAmlCase, listAmlCases, refundAmlCase, rejectAmlCase } from "@/api/aml";
import { ApiError } from "@/api/client";
import { type UnattachedAmlAction } from "@/api/unattached-deposits";
import { UnattachedAmlDialog } from "@/components/unattached-aml-dialog";
import { type AmlCaseDeposit, type AmlCaseDetailResponse, type AmlCaseListItem } from "@/api/types";

// Real implementation of Task 14d: case list + case-detail dialog with the
// three resolution actions (Reject/Refund/Accept) as their own confirmation
// dialogs, each with an optional note. Whole page is AML_OFFICER-gated by
// the shell's nav (see _app.tsx's NAV_ITEMS) and by the backend itself — no
// separate viewer/manager split like merchants (there's no read-only role
// for this queue). Replaces the Task 14a placeholder.
export const Route = createFileRoute("/_app/high-risk")({
  component: HighRiskPage,
});

// ─── formatting helpers ─────────────────────────────────────────────────────
// Same convention as _app.invoices.tsx's (Task 14c) — duplicated locally
// rather than shared, matching that route's own precedent.

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-[#6B7280]">{label}</dt>
      <dd className="font-medium text-[#111827]" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

// Shared box styles for inline dialog messaging: amber "guidance" (the
// unfreeze precondition, and the STILL_FROZEN/transient errors that echo
// it) vs. red "error" (everything else).
const GUIDANCE_BOX = "rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-3 py-2 text-sm text-[#92400E]";
const ERROR_BOX = "rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]";

// ─── deposits table ──────────────────────────────────────────────────────────

function amlSummaryLines(deposit: AmlCaseDeposit): string[] {
  const { amlSummary } = deposit;
  const lines: string[] = [];
  if (amlSummary.provider) lines.push(`Provider: ${amlSummary.provider}`);
  if (amlSummary.status) lines.push(`Status: ${amlSummary.status}`);
  if (amlSummary.riskLevel) lines.push(`Risk: ${amlSummary.riskLevel}`);
  if (amlSummary.categories && amlSummary.categories.length > 0) {
    lines.push(`Categories: ${amlSummary.categories.join(", ")}`);
  }
  if (amlSummary.screenedAt) lines.push(`Screened: ${formatTimestamp(amlSummary.screenedAt)}`);
  return lines;
}

function DepositRow({
  deposit,
  symbol,
  network,
}: {
  deposit: AmlCaseDeposit;
  symbol: string;
  network: AssetNetwork;
}) {
  const summaryLines = amlSummaryLines(deposit);

  return (
    <tr className="border-t border-[#F3F4F6] align-top">
      <td className="px-3 py-2">
        <StatusBadge
          label={deposit.isFrozen ? "Frozen" : "Cleared"}
          color={deposit.isFrozen ? "danger" : "success"}
        />
      </td>
      <td className="px-3 py-2 text-[#6B7280]">{formatStatusLabel(deposit.status)}</td>
      <td className="px-3 py-2 text-right">
        <Amount native={deposit.amount} symbol={symbol} />
      </td>
      <td className="px-3 py-2 text-[#6B7280]">
        <HashLink value={deposit.txHash} type="tx" network={network} />
      </td>
      <td className="px-3 py-2 text-[#6B7280]">{formatTimestamp(deposit.lastEventAt)}</td>
      <td className="px-3 py-2 text-[#111827]">
        {summaryLines.length === 0 ? (
          <span className="text-[#6B7280]">—</span>
        ) : (
          <ul className="space-y-0.5">
            {summaryLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </td>
      <td className="px-3 py-2">
        {deposit.amlScreeningResult ? (
          <details>
            <summary className="cursor-pointer select-none text-xs font-medium text-[#0F172A]">
              Raw JSON
            </summary>
            <pre className="mt-2 max-h-64 max-w-xs overflow-auto rounded-md bg-[#0F172A] p-3 text-[11px] text-[#E2E8F0]">
              {JSON.stringify(deposit.amlScreeningResult, null, 2)}
            </pre>
          </details>
        ) : (
          <span className="text-[#6B7280]">—</span>
        )}
      </td>
    </tr>
  );
}

// ─── action dialogs ──────────────────────────────────────────────────────────
//
// A regular Dialog (not AlertDialog) for all three — each needs a multi-line
// note textarea plus its own state, which doesn't fit Radix's AlertDialog
// content comfortably (see _app.merchants.tsx's AlertDialog usage for the
// simpler single-click-confirm case this page deliberately does NOT reuse
// here). Kept as three separate components rather than one parametrized
// dialog: each action's success/error handling is genuinely different
// (different response shape for Accept, different error codes/guidance per
// action), and three small components read more clearly than one with a
// branching config object.

interface ActionDialogProps {
  invoiceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function RejectDialog({
  invoiceId,
  open,
  onOpenChange,
  onSuccess,
}: ActionDialogProps & { onSuccess: (data: AmlCaseDetailResponse) => void }) {
  const [note, setNote] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNote("");
      setErrorMessage(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => rejectAmlCase(invoiceId, note.trim() || undefined),
    onSuccess: (data) => {
      toast.success("Case rejected.");
      onSuccess(data);
    },
    onError: (err) => {
      setErrorMessage(err instanceof ApiError ? err.message : "Failed to reject case.");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject this case?</DialogTitle>
          <DialogDescription>
            The invoice becomes REJECTED. This cannot be undone — there is no way to reverse a
            rejection from the backoffice.
          </DialogDescription>
        </DialogHeader>

        {errorMessage && <div className={ERROR_BOX}>{errorMessage}</div>}

        <div className="space-y-1.5">
          <Label htmlFor="reject-note">Note (optional)</Label>
          <Textarea
            id="reject-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason for rejecting this case…"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reject case"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RefundDialog({
  invoiceId,
  open,
  onOpenChange,
  onSuccess,
}: ActionDialogProps & { onSuccess: (data: AmlCaseDetailResponse) => void }) {
  const [note, setNote] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isStillFrozen, setIsStillFrozen] = useState(false);

  useEffect(() => {
    if (open) {
      setNote("");
      setErrorMessage(null);
      setIsStillFrozen(false);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => refundAmlCase(invoiceId, note.trim() || undefined),
    onSuccess: (data) => {
      toast.success("Refund opened.");
      onSuccess(data);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrorMessage(err.message);
        setIsStillFrozen(err.code === "STILL_FROZEN");
      } else {
        setErrorMessage("Failed to open refund.");
        setIsStillFrozen(false);
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Open a refund for this case?</DialogTitle>
          <DialogDescription>
            A new OPEN refund is created for this invoice; its status/sub-status are left unchanged
            — the ordinary claim/executor pipeline takes it from here.
          </DialogDescription>
        </DialogHeader>

        <div className={GUIDANCE_BOX}>
          Unfreeze the deposit in the Fireblocks Console first, then retry — a refund cannot open
          while any deposit on this invoice is still frozen.
        </div>

        {errorMessage && <div className={isStillFrozen ? GUIDANCE_BOX : ERROR_BOX}>{errorMessage}</div>}

        <div className="space-y-1.5">
          <Label htmlFor="refund-note">Note (optional)</Label>
          <Textarea
            id="refund-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason for refunding this case…"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Open refund"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcceptDialog({
  invoiceId,
  open,
  onOpenChange,
  onSuccess,
}: ActionDialogProps & { onSuccess: () => void }) {
  const [note, setNote] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // STILL_FROZEN and the 503 "temporarily unavailable" case are both
  // guidance/transient conditions, not business-rule rejections — rendered
  // in the same amber box as the precondition copy above, distinct from a
  // generic red error (e.g. INELIGIBLE, NOT_FOUND).
  const [isGuidance, setIsGuidance] = useState(false);

  useEffect(() => {
    if (open) {
      setNote("");
      setErrorMessage(null);
      setIsGuidance(false);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => acceptAmlCase(invoiceId, note.trim() || undefined),
    onSuccess: () => {
      toast.success("Case accepted.");
      onSuccess();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrorMessage(err.message);
        setIsGuidance(err.status === 503 || err.code === "STILL_FROZEN");
      } else {
        setErrorMessage("Failed to accept case.");
        setIsGuidance(false);
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Accept this case?</DialogTitle>
          <DialogDescription>
            An exact payment settles to COMPLETED through the ordinary pipeline; a short, over, or
            late payment enters the merchant&apos;s normal resolution flow instead.
          </DialogDescription>
        </DialogHeader>

        <div className={GUIDANCE_BOX}>
          Unfreeze the deposit in the Fireblocks Console first, and confirm the case view shows it
          Cleared, before confirming Accept here.
        </div>

        {errorMessage && <div className={isGuidance ? GUIDANCE_BOX : ERROR_BOX}>{errorMessage}</div>}

        <div className="space-y-1.5">
          <Label htmlFor="accept-note">Note (optional)</Label>
          <Textarea
            id="accept-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note…"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept case"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── case detail dialog ──────────────────────────────────────────────────────

interface CaseDetailDialogProps {
  invoiceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CaseDetailDialog({ invoiceId, open, onOpenChange }: CaseDetailDialogProps) {
  const queryClient = useQueryClient();
  const [caseDetail, setCaseDetail] = useState<AmlCaseDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<"reject" | "refund" | "accept" | null>(null);

  useEffect(() => {
    if (!open || !invoiceId) return;

    let cancelled = false;
    setCaseDetail(null);
    setError(null);
    setLoading(true);

    getAmlCase(invoiceId)
      .then((data) => {
        if (!cancelled) setCaseDetail(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Failed to load case.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, invoiceId]);

  // Closing the case-detail dialog also closes any open action sub-dialog,
  // so reopening later (a different row, or the same one) starts clean.
  useEffect(() => {
    if (!open) setActiveAction(null);
  }, [open]);

  const invalidateQueue = () => queryClient.invalidateQueries({ queryKey: ["aml-cases"] });

  // Reject/Refund: same case-detail response shape as the GET — update the
  // dialog in place with it. A rejected case will simply not reappear next
  // time the queue list is (re)fetched; a refunded case's status/subStatus
  // are unchanged so it may still legitimately be in the queue.
  const handleRejectOrRefundSuccess = (data: AmlCaseDetailResponse) => {
    setCaseDetail(data);
    setActiveAction(null);
    invalidateQueue();
  };

  // Accept: deliberately does NOT try to keep showing case-detail data — the
  // accept response doesn't have it (see AcceptAmlCaseResponse). Close both
  // dialogs and let the queue list re-fetch.
  const handleAcceptSuccess = () => {
    setActiveAction(null);
    onOpenChange(false);
    invalidateQueue();
  };

  const isEligible =
    caseDetail?.status === InvoiceStatus.ACTION_REQUIRED && caseDetail?.subStatus === InvoiceSubStatus.HIGH_RISK;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[95vh] max-w-4xl overflow-y-auto">
          {loading && <div className="py-10 text-center text-sm text-[#6B7280]">Loading case…</div>}
          {error && !loading && <div className="py-10 text-center text-sm text-red-600">{error}</div>}

          {caseDetail && !loading && !error && (
            <>
              <DialogHeader>
                <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
                  <DialogTitle>{caseDetail.merchant.businessName}</DialogTitle>
                  <StatusBadge
                    label={formatStatusLabel(caseDetail.subStatus ?? caseDetail.status)}
                    color={INVOICE_STATUS_COLOR[caseDetail.status]}
                  />
                </div>
                <DialogDescription asChild>
                  <div className="flex flex-col gap-0.5">
                    <span className="font-mono text-xs font-semibold">{caseDetail.publicId}</span>
                    <span>{caseDetail.merchant.email}</span>
                  </div>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <section className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-4">
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                    Summary
                  </h3>
                  <div className="mb-4 flex flex-wrap items-end justify-between gap-4 border-b border-[#E5E7EB] pb-4">
                    <div>
                      <div className="text-2xl font-semibold tracking-tight text-[#111827]">
                        <FiatAmount amount={caseDetail.fiatAmount} currency={caseDetail.fiatCurrency} />
                      </div>
                      <div className="mt-0.5 text-[#6B7280]">
                        <Amount
                          native={caseDetail.cryptoAmount}
                          symbol={caseDetail.asset.symbol}
                          usd={caseDetail.cryptoAmountUsd}
                        />
                      </div>
                    </div>
                    <AssetLabel symbol={caseDetail.asset.symbol} network={caseDetail.asset.network} />
                  </div>
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                    <DetailRow
                      label="Fee"
                      value={
                        <Amount
                          native={caseDetail.cryptoFee}
                          symbol={caseDetail.asset.symbol}
                          usd={caseDetail.feeUsd}
                        />
                      }
                    />
                    <DetailRow label="Created" value={formatTimestamp(caseDetail.createdAt)} />
                    <DetailRow label="Expires" value={formatTimestamp(caseDetail.expiresAt)} />
                  </dl>
                </section>

                {caseDetail.priorAudit && (
                  <section className="rounded-lg border border-[#E5E7EB] p-4">
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                      Prior resolution
                    </h3>
                    <dl className="space-y-2">
                      <DetailRow label="Resolved by" value={caseDetail.priorAudit.resolvedBy.displayName} />
                      <DetailRow label="Resolved at" value={formatTimestamp(caseDetail.priorAudit.resolvedAt)} />
                      <DetailRow label="Action" value={formatStatusLabel(caseDetail.priorAudit.action)} />
                      <DetailRow label="Note" value={caseDetail.priorAudit.note ?? "—"} />
                    </dl>
                  </section>
                )}

                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                    Deposits
                  </h3>
                  {caseDetail.deposits.length === 0 ? (
                    <p className="text-sm text-[#6B7280]">No deposits.</p>
                  ) : (
                    <div className="overflow-hidden overflow-x-auto rounded-lg border border-[#E5E7EB]">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-[#F9FAFB] text-left font-semibold text-[#6B7280]">
                            <th className="px-3 py-2">Freeze state</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2 text-right">Amount</th>
                            <th className="px-3 py-2">Tx hash</th>
                            <th className="px-3 py-2">Last event</th>
                            <th className="px-3 py-2">AML summary</th>
                            <th className="px-3 py-2">Raw result</th>
                          </tr>
                        </thead>
                        <tbody>
                          {caseDetail.deposits.map((deposit) => (
                            <DepositRow
                              key={deposit.id}
                              deposit={deposit}
                              symbol={caseDetail.asset.symbol}
                              network={caseDetail.asset.network}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                    Refunds
                  </h3>
                  {caseDetail.refunds.length === 0 ? (
                    <p className="text-sm text-[#6B7280]">No refunds.</p>
                  ) : (
                    <div className="overflow-hidden overflow-x-auto rounded-lg border border-[#E5E7EB]">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-[#F9FAFB] text-left font-semibold text-[#6B7280]">
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Initiator</th>
                            <th className="px-3 py-2">Claimant</th>
                            <th className="px-3 py-2 text-right">Amount</th>
                            <th className="px-3 py-2">Address</th>
                            <th className="px-3 py-2">Claimed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {caseDetail.refunds.map((refund) => (
                            <tr key={refund.id} className="border-t border-[#F3F4F6]">
                              <td className="px-3 py-2">
                                <StatusBadge
                                  label={formatStatusLabel(refund.status)}
                                  color={REFUND_STATUS_COLOR[refund.status]}
                                />
                              </td>
                              <td className="px-3 py-2 text-[#6B7280]">{formatStatusLabel(refund.initiator)}</td>
                              <td className="px-3 py-2 text-[#6B7280]">
                                {refund.claimant ? formatStatusLabel(refund.claimant) : "—"}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <Amount native={refund.amount} symbol={caseDetail.asset.symbol} />
                              </td>
                              <td className="px-3 py-2 text-[#6B7280]">
                                <HashLink value={refund.address} type="address" network={caseDetail.asset.network} />
                              </td>
                              <td className="px-3 py-2 text-[#6B7280]">{formatTimestamp(refund.claimedAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                {isEligible ? (
                  <div className="flex flex-wrap justify-end gap-2 border-t border-[#E5E7EB] pt-4">
                    <Button variant="destructive" onClick={() => setActiveAction("reject")}>
                      Reject
                    </Button>
                    <Button variant="outline" onClick={() => setActiveAction("refund")}>
                      Refund
                    </Button>
                    <Button onClick={() => setActiveAction("accept")}>Accept</Button>
                  </div>
                ) : (
                  <div className="border-t border-[#E5E7EB] pt-4 text-xs text-[#6B7280]">
                    This case is no longer awaiting resolution — Reject/Refund/Accept only apply to
                    ACTION_REQUIRED/HIGH_RISK invoices.
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {caseDetail && (
        <>
          <RejectDialog
            invoiceId={caseDetail.id}
            open={activeAction === "reject"}
            onOpenChange={(next) => setActiveAction(next ? "reject" : null)}
            onSuccess={handleRejectOrRefundSuccess}
          />
          <RefundDialog
            invoiceId={caseDetail.id}
            open={activeAction === "refund"}
            onOpenChange={(next) => setActiveAction(next ? "refund" : null)}
            onSuccess={handleRejectOrRefundSuccess}
          />
          <AcceptDialog
            invoiceId={caseDetail.id}
            open={activeAction === "accept"}
            onOpenChange={(next) => setActiveAction(next ? "accept" : null)}
            onSuccess={handleAcceptSuccess}
          />
        </>
      )}
    </>
  );
}

// ─── row → dialog-opening click ─────────────────────────────────────────────
//
// DataTable has no onRowClick concept (out of scope to modify) and there's no
// route to navigate to here (just a dialog to open) — same cell-filling
// pattern as _app.invoices.tsx/_app.merchants.tsx's `RowLink`, but a `<button>`
// calling back into the page's `setSelectedId` instead of a `<Link>`.

function RowButton({ onSelect, children }: { onSelect: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onSelect} className="-m-2 block w-full p-2 text-left">
      {children}
    </button>
  );
}

// ─── high-risk queue page ────────────────────────────────────────────────────
//
// Real implementation of Task 27 Part C: restyle only — the outer list table
// (formerly a hand-rolled <table>) now goes through the shared DataTable.
// `listAmlCases()` has no pagination/filter/sort support at all (returns the
// full array, always), so this drives DataTable client-side exactly like
// _app.merchants.tsx's Task 25 precedent: a plain local `useState` for
// `page`/`pageSize` only (no sort, no filters, no `useTableState` — there's
// nothing to persist in the URL). Every dialog/action/mutation above this
// point is unchanged.

function HighRiskPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // feature-037: a held unattached deposit and the action picked for it.
  const [unattachedAction, setUnattachedAction] = useState<{ id: string; action: UnattachedAmlAction } | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const select = (item: AmlCaseListItem) =>
    item.unattachedDeposit
      ? void navigate({ to: "/unattached-deposits/$unattachedDepositId", params: { unattachedDepositId: item.unattachedDeposit.id } })
      : setSelectedId(item.invoice.id);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isPending, isError } = useQuery({
    queryKey: ["aml-cases"],
    queryFn: listAmlCases,
  });

  const rows: AmlCaseListItem[] = data ?? [];
  const total = rows.length;
  const pageItems = rows.slice((page - 1) * pageSize, page * pageSize);

  const columns: DataTableColumn<AmlCaseListItem>[] = [
    {
      key: "merchant",
      header: "Merchant",
      // Not a RowButton like the rest of the row (which opens the AML case
      // detail dialog) — this cell navigates to the merchant page instead.
      cell: (item) => (
        <MerchantCell
          merchantId={item.merchant.id}
          businessName={item.merchant.businessName}
          publicId={item.merchant.publicId}
        />
      ),
    },
    {
      // feature-037: invoice cases and held unattached deposits share the queue.
      key: "kind",
      header: "Case",
      cell: (item) => (
        <RowButton onSelect={() => select(item)}>
          {item.unattachedDeposit ? (
            <span>
              Unattached deposit <span className="font-mono">{item.unattachedDeposit.publicId}</span>
            </span>
          ) : (
            "Invoice"
          )}
        </RowButton>
      ),
    },
    {
      key: "invoicePublicId",
      header: "Invoice ID",
      cell: (item) => (
        <RowButton onSelect={() => select(item)}>
          <span className="font-mono font-semibold">{item.invoice.publicId}</span>
        </RowButton>
      ),
    },
    {
      key: "fiatAmount",
      header: "Amount",
      cell: (item) => (
        <RowButton onSelect={() => select(item)}>
          {/* An unattached case is decided on the held deposit, not the original invoice. */}
          {item.unattachedDeposit ? (
            `${item.unattachedDeposit.cryptoAmount} ${item.unattachedDeposit.assetSymbol}`
          ) : (
            <FiatAmount amount={item.invoice.fiatAmount} currency={item.invoice.fiatCurrency} />
          )}
        </RowButton>
      ),
    },
    {
      key: "flaggedAt",
      header: "Flagged",
      cell: (item) => (
        <RowButton onSelect={() => select(item)}>
          {item.flaggedAt ? formatTimestamp(item.flaggedAt) : "—"}
        </RowButton>
      ),
    },
    {
      key: "actions",
      header: "",
      cell: (item) =>
        item.unattachedDeposit ? (
          <div className="flex gap-1.5">
            {(["accept", "refund", "reject"] as const).map((action) => (
              <Button
                key={action}
                size="sm"
                variant={action === "reject" ? "destructive" : action === "refund" ? "outline" : "default"}
                onClick={() => setUnattachedAction({ id: item.unattachedDeposit!.id, action })}
              >
                {action[0].toUpperCase() + action.slice(1)}
              </Button>
            ))}
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={pageItems}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={isPending}
        error={isError ? "Failed to load the high-risk queue." : null}
        onPageChange={(nextPage) => setPage(nextPage)}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setPage(1);
        }}
        csvExport={{
          onExport: () =>
            exportCsv({
              columns: [
                { header: "Case", value: (item: AmlCaseListItem) => item.kind },
                { header: "Merchant", value: (item: AmlCaseListItem) => item.merchant.businessName },
                { header: "Invoice ID", value: (item: AmlCaseListItem) => item.invoice.publicId },
                {
                  header: "Unattached deposit ID",
                  value: (item: AmlCaseListItem) => item.unattachedDeposit?.publicId ?? "",
                },
                {
                  header: "Amount",
                  value: (item: AmlCaseListItem) =>
                    item.unattachedDeposit
                      ? `${item.unattachedDeposit.cryptoAmount} ${item.unattachedDeposit.assetSymbol}`
                      : formatFiatAmount(item.invoice.fiatAmount, item.invoice.fiatCurrency),
                },
                {
                  header: "Flagged",
                  value: (item: AmlCaseListItem) => (item.flaggedAt ? formatTimestamp(item.flaggedAt) : ""),
                },
              ],
              // listAmlCases() takes no params and always returns the full,
              // unpaginated array — one call covers every row; exportCsv's
              // loop stops itself once rows.length reaches the reported total.
              fetchPage: () => listAmlCases().then((allRows) => ({ data: allRows, total: allRows.length })),
              filenamePrefix: "high-risk-queue",
            }),
        }}
        getRowKey={(item) => item.unattachedDeposit?.id ?? item.invoice.id}
        emptyMessage="No cases in the queue."
      />

      <UnattachedAmlDialog
        depositId={unattachedAction?.id ?? ""}
        action={unattachedAction?.action ?? null}
        onOpenChange={(open) => !open && setUnattachedAction(null)}
        onSuccess={() => {
          setUnattachedAction(null);
          void queryClient.invalidateQueries({ queryKey: ["aml-cases"] });
        }}
      />

      <CaseDetailDialog
        invoiceId={selectedId}
        open={selectedId !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      />
    </div>
  );
}
